import React,{useEffect,useState} from 'react'
import { MwInputText,MwSelector,MwSwitch } from '../../ui'
import { useCurrent, useFetch } from '../../../hooks'
import { useTranslation } from 'react-i18next';
import {
    PRODUCT_FORM_SECTION_CLASS,
    PRODUCT_OPTION_LABEL_CLASS,
    PRODUCT_OPTION_ROW_CLASS,
    normalizeApiCollection,
} from '../productUi';

const StockAndPrice = ({
    productCode,
    setProductCode,
    productPrice,
    setProductPrice,
    productBarcode,
    setProductBarcode,
    productFactoryCode,
    setProductFactoryCode,
    isStock,
    setIsStock,
    codeInStock,
    setCodeInStock,
    qty,
    setQty,
    cost,
    setCost,
    weightProduct,
    setWeightProduct,
    sizeProduct,
    setSizeProduct,
    errors,
    uomTemplateSelected,
    setTemplateUomSelected,
    uomSaleSelected,
    setUomSaleSelected,
    uomPurchaseSelected,
    setUomPurchesSelected
}) => {
    const {currentTenantId,currentCompanyId,currentLangId,currentBranchId} = useCurrent();
    const [warehousesList, setWarehousesList] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);

    const {data:uomData,loading:uomLoading} = useFetch(`${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/inventory/uom/tenant/${currentTenantId}`)
    const {data: warehousesData, loading: warehousesLoading} = useFetch(`${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/warehouses/${currentTenantId}/${currentCompanyId}/${currentBranchId}`)
    const [uomTemplateList,setTemplateUomList] = useState([]);
    const [uomList,setUomList] = useState([]);
    const [t] = useTranslation('global')

    useEffect(() => {
        if(warehousesData && !warehousesLoading) {
            const warehouses = normalizeApiCollection(warehousesData).map((warehouse) => {
                let displayName;
                if(currentLangId === 'ar' && warehouse.name_ar) {
                    displayName = warehouse.name_ar;
                } else if(currentLangId === 'en' && warehouse.name_en) {
                    displayName = warehouse.name_en;
                } else {
                    displayName = warehouse.name_ar || warehouse.name_en || warehouse.code;
                }
                
                return {
                    label: `${displayName} (${warehouse.code})`,
                    value: warehouse.code, 
                    lang: currentLangId,
                    data: warehouse
                };
            });

            setWarehousesList(warehouses);
            
            if(codeInStock) {
                const matchingWarehouse = warehouses.find((warehouse) => warehouse.value === codeInStock);
                if(matchingWarehouse) {
                    setSelectedWarehouse(matchingWarehouse);
                }
            }
        }
    }, [warehousesData, warehousesLoading, currentLangId, codeInStock]);
    
    useEffect(() => {
        if(selectedWarehouse) {
            setCodeInStock(selectedWarehouse.data.code);
        }
    }, [selectedWarehouse, setCodeInStock]);

    useEffect(() => {
        if(uomData && !uomLoading ){
            const itemList = normalizeApiCollection(uomData).map((el) => ({
                label:el?.name,
                value:el?.id,
                lang:currentLangId,
                data:el,
            }));
            setTemplateUomList(itemList);

            const uomDetails = uomTemplateSelected?.data?.uom_master?.uom_details || [];
            const itemList2 = uomDetails.map((el) => ({
                label:el?.short_name,
                value:el?.id,
                lang:currentLangId,
                data:el,
            }));
            setUomList(itemList2);
        }
    },[uomData,uomTemplateSelected,currentLangId,uomLoading]);

    return (
        <div className='grid gap-4 lg:grid-cols-2'>
            <div className={`${PRODUCT_FORM_SECTION_CLASS} space-y-3`}>
                <MwInputText
                    label={t('product.product_code')} 
                    id='productCode' 
                    value={productCode} 
                    onChange={(e)=>setProductCode(e.target.value)}
                    placeholder={t('product.product_code')}
                />
                <MwSelector
                    label={t('product.unit_template')} 
                    initalValue={t('product.choose_unit_template')} 
                    dataType='labelValue'
                    _data={uomTemplateList}
                    withAddNew={false}
                    selectedItem={uomTemplateSelected}
                    setSelectedItem={setTemplateUomSelected}
                    className='z-10'
                />
                <div className='relative'>
                    <MwInputText
                        label={t('product.sale_price')} 
                        id='productPrice' 
                        invalid={!!errors[`price`]}
                        invalidMsg={errors[`price`]}
                        placeholder={t('product.sale_price')}
                        value={productPrice} 
                        onChange={(e)=>setProductPrice(e.target.value)}
                    />
                    { uomTemplateSelected && <div className='relative flex flex-row-reverse'>
                        <MwSelector
                            label={t('product.salesUnit')}
                            initalValue={t('product.choose_unit')} 
                            dataType='labelValue'
                            _data={uomList}
                            withAddNew={false}
                            selectedItem={uomSaleSelected}
                            setSelectedItem={setUomSaleSelected}
                            className='absolute -top-8 end-0 z-20 w-32'
                        />
                    </div>}
                </div>
                <div className='relative'>
                    <MwInputText
                        label={t('product.purchase_price')} 
                        id='cost' 
                        invalid={!!errors[`cost`]}
                        invalidMsg={errors[`cost`]}
                        placeholder={t('product.purchase_price')}
                        value={cost} 
                        onChange={(e)=>setCost(e.target.value)}
                    />
                    { uomTemplateSelected && <div className='relative flex flex-row-reverse'>
                        <MwSelector
                            label={t('product.purchaseUnit')}
                            initalValue={t('product.choose_unit')} 
                            dataType='labelValue'
                            _data={uomList}
                            withAddNew={false}
                            selectedItem={uomPurchaseSelected}
                            setSelectedItem={setUomPurchesSelected}
                            className='absolute -top-8 end-0 z-10 w-32'
                        />
                    </div>}
                </div>
                <MwInputText
                    label={t('product.product_barcode')} 
                    id='productBarcode' 
                    value={productBarcode} 
                    placeholder={t('product.product_barcode')}
                    onChange={(e)=>setProductBarcode(e.target.value)}
                />
                <MwInputText
                    label={t('product.factory_code')} 
                    id='productFactoryCode' 
                    value={productFactoryCode} 
                    placeholder={t('product.factory_code')}
                    onChange={(e)=>setProductFactoryCode(e.target.value)}
                />
            </div>
            <div className='space-y-4'>
                <div className={PRODUCT_OPTION_ROW_CLASS}>
                    <label className={PRODUCT_OPTION_LABEL_CLASS}>  {t('product.warehouse_usage')} </label>
                    <MwSwitch custKey='isStock' isActive={isStock} setIsActive={setIsStock} onChangeSwitch={() => setIsStock(!isStock)} />
                </div>
                <div className={`${PRODUCT_FORM_SECTION_CLASS} space-y-3`}>
                    {isStock && (
                        <>
                            <MwSelector
                                label={t('product.warehouse_code')}
                                initalValue={t('product.selectWarehouse')}
                                dataType='labelValue'
                                _data={warehousesList}
                                withAddNew={false}
                                selectedItem={selectedWarehouse}
                                setSelectedItem={setSelectedWarehouse}
                                className='z-10'
                                withSearch={true}
                            />
                            <MwInputText
                                inputType='number'
                                label={t('product.quantity')} 
                                placeholder={t('product.quantity')}
                                id='qty' 
                                value={qty} 
                                onChange={(e)=>setQty(e.target.value)}
                            />
                        </>
                    )}
                    <div className='grid gap-3 md:grid-cols-2'>
                        <MwInputText
                            label={t('product.weight')} 
                            id='weightProduct' 
                            placeholder={t('product.weight')}
                            value={weightProduct} 
                            onChange={(e)=>setWeightProduct(e.target.value)}
                        />
                        <MwInputText
                            label={t('product.size')} 
                            id='sizeProduct' 
                            placeholder={t('product.size')}
                            value={sizeProduct} 
                            onChange={(e)=>setSizeProduct(e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StockAndPrice
